import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Chip,
  Divider,
  Autocomplete,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Print as PrintIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Storefront as StorefrontIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import QuotePrint from './QuotePrint';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { getProductImage } from '../../utils/productUtils';

const isInStockForQuoteSearch = (product) => (parseInt(product?.stock, 10) || 0) > 0;

const productsEligibleForAdvancedSearch = (list) =>
  (Array.isArray(list) ? list : []).filter(isInStockForQuoteSearch);

const QuoteDialog = ({ 
  open, 
  onClose, 
  onSave, 
  customer, 
  vehicle, 
  maintenanceData,
  maintenanceId,
  saveOnly = false,
  editMode = false,
  initialData = null
}) => {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [quoteItems, setQuoteItems] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [includeLabor, setIncludeLabor] = useState(false);
  const [laborRate, setLaborRate] = useState(25); // Tariffa oraria predefinita
  const [errors, setErrors] = useState({});
  const [quoteNumber, setQuoteNumber] = useState('');
  const [openCustomProductDialog, setOpenCustomProductDialog] = useState(false);
  const [customProduct, setCustomProduct] = useState({
    code: '',
    description: '',
    unitPrice: '',
    costPrice: '',
    quantity: 1,
    needsOrder: false
  });
  const [customProductErrors, setCustomProductErrors] = useState({});
  const [customProductMode, setCustomProductMode] = useState('add'); // 'add' o 'edit'
  const [editingItemId, setEditingItemId] = useState(null);
  const [openAdvancedSearch, setOpenAdvancedSearch] = useState(false);
  const [advancedSearchTerm, setAdvancedSearchTerm] = useState('');
  const [advancedSearchResults, setAdvancedSearchResults] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [openB2bSearch, setOpenB2bSearch] = useState(false);
  const [b2bSearchTerm, setB2bSearchTerm] = useState('');
  const [b2bResults, setB2bResults] = useState([]);
  const [b2bLoading, setB2bLoading] = useState(false);
  const [b2bError, setB2bError] = useState(null);
  const [b2bSelectedItems, setB2bSelectedItems] = useState([]);

  // Calcola i totali
  const calculateTotals = () => {
    // I prezzi del magazzino sono già IVA inclusa, quindi il totale è direttamente la somma
    const total = quoteItems.reduce((sum, item) => {
      const itemTotal = item.quantity * item.unitPrice;
      const discountAmount = parseFloat(item.discount) || 0; // Sconto in euro invece che percentuale
      return sum + (itemTotal - discountAmount);
    }, 0);

    // Calcoliamo l'IVA inclusa nel totale
    const taxRate = 22; // IVA 22%
    const subtotal = total / (1 + taxRate / 100); // Prezzo senza IVA
    const taxAmount = total - subtotal; // IVA inclusa

    return { 
      subtotal: parseFloat(subtotal.toFixed(2)), 
      taxAmount: parseFloat(taxAmount.toFixed(2)), 
      total: parseFloat(total.toFixed(2)), 
      taxRate 
    };
  };

  const totals = calculateTotals();

  useEffect(() => {
    if (open) {
      fetchProducts();
      
      if (editMode && initialData) {
        // Modalità modifica: carica dati esistenti
        setQuoteItems(initialData.items || []);
        setLaborRate(initialData.laborRate || 25);
        // Non generare nuovo numero preventivo
      } else {
        // Modalità crea nuovo
        generateQuoteNumber();
        setQuoteItems([]);
        setLaborRate(25);
      }
      
      setSearchTerm('');
      setSelectedProduct(null);
      setQuantity(1);
      setIncludeLabor(false);
      setErrors({});
    }
  }, [open, editMode, initialData]);

  useEffect(() => {
    // Assicuriamoci che products sia sempre un array
    if (!Array.isArray(products)) {
      setFilteredProducts([]);
      return;
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const filtered = products.filter(product =>
        (product.name && product.name.toLowerCase().includes(searchLower)) ||
        (product.code && product.code.toLowerCase().includes(searchLower)) ||
        (product.description && product.description.toLowerCase().includes(searchLower)) ||
        (product.brand && product.brand.toLowerCase().includes(searchLower))
      );
      setFilteredProducts(filtered);
    } else {
      // Mostra tutti i prodotti quando non c'è ricerca
      setFilteredProducts(products);
    }
  }, [searchTerm, products]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      // Richiediamo tutti i prodotti senza paginazione
      const response = await api.get('/products?all=true');
      // L'API con all=true restituisce direttamente l'array di prodotti
      const productsData = response.data;
      setProducts(Array.isArray(productsData) ? productsData : []);
    } catch (error) {
      console.error('Errore nel caricamento prodotti:', error);
      setErrors({ general: 'Errore nel caricamento dei prodotti' });
      setProducts([]); // Assicuriamoci che products sia sempre un array
    } finally {
      setLoading(false);
    }
  };

  const generateQuoteNumber = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    setQuoteNumber(`PREV-${year}${month}${day}-${random}`);
  };

  const handleAddProduct = () => {
    if (!selectedProduct) {
      setErrors({ product: 'Seleziona un prodotto' });
      return;
    }

    if (quantity <= 0) {
      setErrors({ quantity: 'La quantità deve essere maggiore di 0' });
      return;
    }

    const existingItemIndex = quoteItems.findIndex(item => item.productId === selectedProduct.id);
    
    if (existingItemIndex >= 0) {
      // Aggiorna la quantità se il prodotto esiste già
      const updatedItems = [...quoteItems];
      updatedItems[existingItemIndex].quantity += quantity;
      setQuoteItems(updatedItems);
    } else {
      // Aggiungi nuovo prodotto
      const newItem = {
        id: Date.now(), // ID temporaneo
        productId: selectedProduct.id,
        productCode: selectedProduct.code,
        productName: selectedProduct.name,
        productDescription: selectedProduct.description,
        quantity: quantity,
        unitPrice: includeLabor && selectedProduct.sellingPriceWithAssembly 
          ? selectedProduct.sellingPriceWithAssembly 
          : selectedProduct.sellingPrice,
        discount: 0,
        isLabor: selectedProduct.isLabor,
        includesLabor: includeLabor,
        laborTime: selectedProduct.assemblyTime || 0,
        laborRate: laborRate,
        notes: ''
      };
      
      setQuoteItems([...quoteItems, newItem]);
    }

    // Reset form
    setSelectedProduct(null);
    setQuantity(1);
    setIncludeLabor(false);
    setErrors({});
  };

  const handleRemoveItem = (itemId) => {
    setQuoteItems(quoteItems.filter(item => item.id !== itemId));
  };

  const handleOpenAdvancedSearch = () => {
    setAdvancedSearchResults(productsEligibleForAdvancedSearch(products));
    setSelectedItems([]);
    setAdvancedSearchTerm('');
    setOpenAdvancedSearch(true);
  };

  const handleCloseAdvancedSearch = () => {
    setOpenAdvancedSearch(false);
    setAdvancedSearchTerm('');
    setSelectedItems([]);
  };

  const handleAdvancedSearchChange = (e) => {
    const term = e.target.value.toLowerCase();
    setAdvancedSearchTerm(term);
    
    const inStock = productsEligibleForAdvancedSearch(products);
    if (term === '') {
      setAdvancedSearchResults(inStock);
    } else {
      const filtered = inStock.filter(p =>
        (p.name && p.name.toLowerCase().includes(term)) ||
        (p.code && p.code.toLowerCase().includes(term)) ||
        (p.description && p.description.toLowerCase().includes(term)) ||
        (p.brand && p.brand.toLowerCase().includes(term))
      );
      setAdvancedSearchResults(filtered);
    }
  };

  const handleToggleItem = (product) => {
    setSelectedItems(prev => {
      const exists = prev.find(item => item.id === product.id);
      if (exists) {
        return prev.filter(item => item.id !== product.id);
      } else {
        return [...prev, {
          ...product,
          quantity: 1,
          includesLabor: false
        }];
      }
    });
  };

  const handleItemQuantityChange = (productId, quantity) => {
    setSelectedItems(prev => prev.map(item => {
      if (item.id === productId) {
        // Permetti valori vuoti temporaneamente durante la digitazione
        if (quantity === '') {
          return { ...item, quantity: '' };
        } else {
          const numValue = parseInt(quantity);
          if (!isNaN(numValue) && numValue >= 1) {
            return { ...item, quantity: numValue };
          }
        }
      }
      return item;
    }));
  };

  const handleItemQuantityBlur = (productId, quantity) => {
    // Quando l'utente esce dal campo, assicurati che ci sia almeno 1
    const finalQuantity = quantity === '' ? 1 : (parseInt(quantity) || 1);
    setSelectedItems(prev => prev.map(item =>
      item.id === productId ? { ...item, quantity: finalQuantity } : item
    ));
  };

  const handleItemLaborChange = (productId, includesLabor) => {
    setSelectedItems(prev => prev.map(item =>
      item.id === productId ? { ...item, includesLabor } : item
    ));
  };

  const handleAddSelectedItems = () => {
    if (selectedItems.length === 0) return;

    selectedItems.forEach(item => {
      const existingItemIndex = quoteItems.findIndex(qItem => qItem.productId === item.id);
      
      if (existingItemIndex >= 0) {
        // Aggiorna la quantità se il prodotto esiste già
        const updatedItems = [...quoteItems];
        updatedItems[existingItemIndex].quantity += item.quantity;
        setQuoteItems(updatedItems);
      } else {
        // Aggiungi nuovo prodotto
        const newItem = {
          id: Date.now(), // ID temporaneo
          productId: item.id,
          productCode: item.code,
          productName: item.name,
          productDescription: item.description,
          quantity: item.quantity,
          unitPrice: item.includesLabor && item.sellingPriceWithAssembly 
            ? item.sellingPriceWithAssembly 
            : item.sellingPrice,
          discount: 0,
          isLabor: item.isLabor,
          includesLabor: item.includesLabor,
          laborTime: item.assemblyTime || 0,
          laborRate: laborRate,
          notes: ''
        };
        
        setQuoteItems(prev => [...prev, newItem]);
      }
    });

    handleCloseAdvancedSearch();
  };

  const searchB2b = async (term) => {
    const t = (term || '').trim();
    if (!t) {
      setB2bResults([]);
      return;
    }
    setB2bLoading(true);
    setB2bError(null);
    try {
      const res = await api.get('/b2b-products', {
        params: { search: t, limit: 20, page: 1 }
      });
      const list = res.data?.products ?? res.data ?? [];
      setB2bResults(Array.isArray(list) ? list : []);
      if (Array.isArray(list) && list.length === 0) {
        setB2bError('Nessun articolo trovato nello Shop B2B');
      }
    } catch (err) {
      setB2bError('Errore ricerca Shop B2B');
      setB2bResults([]);
    } finally {
      setB2bLoading(false);
    }
  };

  const handleOpenB2bSearch = () => {
    setOpenB2bSearch(true);
    setB2bSearchTerm('');
    setB2bResults([]);
    setB2bSelectedItems([]);
    setB2bError(null);
  };

  const handleCloseB2bSearch = () => {
    setOpenB2bSearch(false);
    setB2bSearchTerm('');
    setB2bResults([]);
    setB2bSelectedItems([]);
    setB2bError(null);
  };

  const handleToggleB2bItem = (product) => {
    setB2bSelectedItems(prev => {
      const exists = prev.find(item => (item.product.id || item.product.code) === (product.id || product.code));
      if (exists) {
        return prev.filter(item => (item.product.id || item.product.code) !== (product.id || product.code));
      }
      return [...prev, {
        product,
        quantity: 1,
        includesLabor: false,
        manualAssemblyPrice: ''
      }];
    });
  };

  const handleB2bQuantityChange = (productKey, value) => {
    setB2bSelectedItems(prev => prev.map(item => {
      const key = item.product.id || item.product.code;
      if (key !== productKey) return item;
      if (value === '') return { ...item, quantity: '' };
      const num = parseInt(value, 10);
      if (!isNaN(num) && num >= 1) return { ...item, quantity: num };
      return item;
    }));
  };

  const handleB2bQuantityBlur = (productKey) => {
    setB2bSelectedItems(prev => prev.map(item => {
      const key = item.product.id || item.product.code;
      if (key !== productKey) return item;
      const q = item.quantity === '' ? 1 : (parseInt(item.quantity, 10) || 1);
      return { ...item, quantity: q };
    }));
  };

  const handleB2bLaborChange = (productKey, includesLabor) => {
    setB2bSelectedItems(prev => prev.map(item =>
      (item.product.id || item.product.code) === productKey ? { ...item, includesLabor } : item
    ));
  };

  const handleB2bAssemblyPriceChange = (productKey, value) => {
    setB2bSelectedItems(prev => prev.map(item =>
      (item.product.id || item.product.code) === productKey ? { ...item, manualAssemblyPrice: value } : item
    ));
  };

  const handleAddB2bItemsToQuote = () => {
    const toAdd = b2bSelectedItems
      .filter(item => item.quantity >= 1)
      .map(sel => {
        const p = sel.product;
        const selling = parseFloat(p.sellingPrice) || 0;
        const assemblyPrice = sel.includesLabor ? (parseFloat(sel.manualAssemblyPrice) || 0) : 0;
        const unitPrice = selling + assemblyPrice;
        return {
          id: Date.now() + Math.random(),
          productId: null,
          productCode: p.code || '',
          productName: p.name || p.code || '',
          productDescription: p.name || p.description || '',
          quantity: typeof sel.quantity === 'number' ? sel.quantity : (parseInt(sel.quantity, 10) || 1),
          unitPrice,
          costPrice: parseFloat(p.purchasePrice) || 0,
          discount: 0,
          isLabor: false,
          includesLabor: !!sel.includesLabor,
          laborTime: 0,
          laborRate: 0,
          notes: '',
          isCustom: false,
          needsOrder: true,
          productImageUrl: (p.image || '').trim() || null
        };
      });
    setQuoteItems(prev => [...prev, ...toAdd]);
    handleCloseB2bSearch();
  };

  const handleUpdateItem = (itemId, field, value) => {
    const updatedItems = quoteItems.map(item => {
      if (item.id === itemId) {
        return { ...item, [field]: value };
      }
      return item;
    });
    setQuoteItems(updatedItems);
  };

  const handlePrint = async () => {
    try {
      // Attendiamo che il contenuto sia renderizzato
      await new Promise(resolve => setTimeout(resolve, 100));

      const element = document.getElementById('quote-print');
      if (!element) {
        console.error('Elemento quote-print non trovato');
        return;
      }

      // Calcoliamo le dimensioni in pixel (1mm = 3.779527559055118 px a 96 DPI)
      const pxPerMm = 3.779527559055118;
      const dimensions = { width: 210, height: 297 }; // A4

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        width: dimensions.width * pxPerMm,
        height: dimensions.height * pxPerMm,
        windowWidth: dimensions.width * pxPerMm,
        windowHeight: dimensions.height * pxPerMm
      });

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [dimensions.width, dimensions.height]
      });

      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(
        imgData,
        'PNG',
        0,
        0,
        dimensions.width,
        dimensions.height,
        '',
        'FAST'
      );

      // Salva il PDF
      pdf.save(`preventivo-${quoteNumber}.pdf`);
    } catch (error) {
      console.error('Errore durante la generazione del PDF:', error);
    }
  };

  const handleSave = async () => {
    if (quoteItems.length === 0) {
      setErrors({ general: 'Aggiungi almeno un prodotto al preventivo' });
      return;
    }

    setLoading(true);
    try {
      const quoteData = {
        quoteNumber,
        customerId: customer.id,
        vehicleId: vehicle.id,
        maintenanceId: maintenanceId || null,
        title: `Preventivo per ${customer.firstName} ${customer.lastName}`,
        description: `Preventivo manutenzione per ${vehicle.brand} ${vehicle.model}`,
        items: quoteItems.map(item => ({
          productId: item.productId,
          productCode: item.productCode,
          productName: item.productName,
          productDescription: item.productDescription,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          costPrice: item.costPrice,
          discount: item.discount,
          isLabor: item.isLabor,
          includesLabor: item.includesLabor,
          laborTime: item.laborTime,
          laborRate: item.laborRate,
          notes: item.notes,
          isCustom: item.isCustom,
          needsOrder: item.needsOrder,
          productImageUrl: item.productImageUrl || null
        })),
        subtotal: totals.subtotal,
        taxAmount: totals.taxAmount,
        total: totals.total,
        maintenanceData
      };

      await onSave(quoteData);
      onClose();
    } catch (error) {
      console.error('Errore nel salvataggio preventivo:', error);
      setErrors({ general: 'Errore nel salvataggio del preventivo' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setQuoteItems([]);
    setSearchTerm('');
    setSelectedProduct(null);
    setQuantity(1);
    setIncludeLabor(false);
    setErrors({});
    setOpenCustomProductDialog(false);
    setCustomProduct({ description: '', unitPrice: '', costPrice: '', quantity: 1, needsOrder: false });
    setCustomProductErrors({});
    onClose();
  };

  // Gestione articolo personalizzato
  const handleOpenCustomProductDialog = () => {
    setCustomProduct({
      description: '',
      unitPrice: '',
      costPrice: '',
      quantity: 1,
      needsOrder: false
    });
    setCustomProductErrors({});
    setOpenCustomProductDialog(true);
  };

  const handleCloseCustomProductDialog = () => {
    setOpenCustomProductDialog(false);
    setCustomProduct({ code: '', description: '', unitPrice: '', costPrice: '', quantity: 1, needsOrder: false });
    setCustomProductErrors({});
    setCustomProductMode('add');
    setEditingItemId(null);
  };

  const handleCustomProductChange = (e) => {
    const { name, value } = e.target;
    setCustomProduct(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is edited
    if (customProductErrors[name]) {
      setCustomProductErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateCustomProduct = () => {
    const errors = {};
    
    // Validazione codice
    if (!customProduct.code.trim()) {
      errors.code = "Il codice è obbligatorio";
    }
    
    // Validazione descrizione
    if (!customProduct.description.trim()) {
      errors.description = "La descrizione è obbligatoria";
    }
    
    // Validazione prezzo unitario
    if (!customProduct.unitPrice) {
      errors.unitPrice = "Il prezzo unitario è obbligatorio";
    } else {
      const unitPrice = parseFloat(customProduct.unitPrice);
      if (isNaN(unitPrice) || unitPrice < 0) {
        errors.unitPrice = "Inserire un prezzo valido maggiore o uguale a 0";
      }
    }
    
    // Validazione prezzo di costo
    if (!customProduct.costPrice) {
      errors.costPrice = "Il prezzo di costo è obbligatorio";
    } else {
      const costPrice = parseFloat(customProduct.costPrice);
      if (isNaN(costPrice) || costPrice < 0) {
        errors.costPrice = "Inserire un prezzo di costo valido maggiore o uguale a 0";
      }
    }
    
    // Validazione quantità
    if (!customProduct.quantity) {
      errors.quantity = "La quantità è obbligatoria";
    } else {
      const quantity = parseInt(customProduct.quantity);
      if (isNaN(quantity) || quantity <= 0) {
        errors.quantity = "Inserire una quantità valida maggiore di 0";
      }
    }
    
    setCustomProductErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddCustomProduct = () => {
    if (!validateCustomProduct()) {
      return;
    }

    const newItem = {
      id: Date.now(), // ID temporaneo
      productId: null, // null per articoli personalizzati
      productCode: customProduct.code,
      productName: customProduct.description,
      productDescription: customProduct.description,
      quantity: parseInt(customProduct.quantity),
      unitPrice: parseFloat(customProduct.unitPrice),
      costPrice: parseFloat(customProduct.costPrice),
      discount: 0,
      isLabor: false,
      includesLabor: false,
      laborTime: 0,
      laborRate: 0,
      notes: '',
      isCustom: true,
      needsOrder: customProduct.needsOrder
    };
    
    if (customProductMode === 'edit' && editingItemId) {
      // Aggiorna articolo esistente
      setQuoteItems(quoteItems.map(item => 
        item.id === editingItemId 
          ? { ...item, ...newItem, id: editingItemId } // Mantieni l'ID originale
          : item
      ));
    } else {
      // Aggiungi nuovo articolo
      setQuoteItems([...quoteItems, newItem]);
    }
    
    // Reset form e chiudi dialog
    setCustomProduct({ code: '', description: '', unitPrice: '', costPrice: '', quantity: 1, needsOrder: false });
    setCustomProductErrors({});
    setCustomProductMode('add');
    setEditingItemId(null);
    setOpenCustomProductDialog(false);
  };

  const handleEditCustomItem = (item) => {
    // Carica i dati dell'articolo nel form
    setCustomProduct({
      code: item.productCode || '',
      description: item.productName || '',
      unitPrice: item.unitPrice || '',
      costPrice: item.costPrice || 0,
      quantity: item.quantity || 1,
      needsOrder: item.needsOrder || false
    });
    
    setCustomProductMode('edit');
    setEditingItemId(item.id);
    setOpenCustomProductDialog(true);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{editMode ? 'Modifica Preventivo' : 'Nuovo Preventivo'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {quoteNumber}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {errors.general && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.general}
          </Alert>
        )}

        {/* Informazioni cliente e veicolo */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Cliente
                </Typography>
                <Typography variant="body1">
                  {customer?.firstName} {customer?.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {customer?.phone}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Veicolo
                </Typography>
                <Typography variant="body1">
                  {vehicle?.brand} {vehicle?.model}
                </Typography>
                {vehicle?.serialNumber && (
                  <Typography variant="body2" color="text.secondary">
                    SN: {vehicle.serialNumber}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Ricerca e selezione prodotti */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Aggiungi Prodotti
          </Typography>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Autocomplete
                  options={filteredProducts}
                  getOptionLabel={(option) => `${option.code} - ${option.name}`}
                  value={selectedProduct}
                  onChange={(event, newValue) => setSelectedProduct(newValue)}
                  sx={{ flex: 1 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cerca prodotto"
                      placeholder="Inserisci codice o nome prodotto"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <SearchIcon sx={{ mr: 1 }} />
                      }}
                      error={!!errors.product}
                      helperText={errors.product}
                    />
                  )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body1">
                        {option.code} - {option.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.brand} • €{option.sellingPrice}
                        {option.sellingPriceWithAssembly && ` (€${option.sellingPriceWithAssembly} con montaggio)`}
                      </Typography>
                      <Typography variant="caption" color={option.stock === 0 ? "error" : option.stock <= 5 ? "warning.main" : "success.main"} sx={{ display: 'block', fontWeight: 'bold' }}>
                        Scorta: {option.stock} {option.stock === 0 ? '(ESAURITO)' : option.stock <= 5 ? '(BASSA)' : ''}
                      </Typography>
                    </Box>
                  </li>
                )}
                />
                <IconButton
                  color="primary"
                  onClick={handleOpenAdvancedSearch}
                  sx={{ mt: 1 }}
                  title="Ricerca avanzata prodotti"
                >
                  <SearchIcon />
                </IconButton>
                <IconButton
                  color="secondary"
                  onClick={handleOpenB2bSearch}
                  sx={{ mt: 1 }}
                  title="Cerca nello Shop B2B"
                >
                  <StorefrontIcon />
                </IconButton>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleOpenCustomProductDialog}
                fullWidth
                sx={{ height: '56px' }}
              >
                Articolo Personalizzato
              </Button>
            </Grid>
            
            <Grid item xs={6} md={1}>
              <TextField
                label="Quantità"
                type="number"
                value={quantity}
                onChange={(e) => {
                  const value = e.target.value;
                  // Permetti valori vuoti temporaneamente durante la digitazione
                  if (value === '') {
                    setQuantity('');
                  } else {
                    const numValue = parseInt(value);
                    if (!isNaN(numValue) && numValue >= 1) {
                      setQuantity(numValue);
                    }
                  }
                }}
                onBlur={(e) => {
                  // Quando l'utente esce dal campo, assicurati che ci sia almeno 1
                  if (quantity === '' || quantity < 1) {
                    setQuantity(1);
                  }
                }}
                inputProps={{ min: 1, step: 1 }}
                error={!!errors.quantity}
                helperText={errors.quantity}
                fullWidth
              />
            </Grid>
            
            <Grid item xs={6} md={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeLabor}
                    onChange={(e) => setIncludeLabor(e.target.checked)}
                  />
                }
                label="Con montaggio"
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddProduct}
                disabled={!selectedProduct || quantity <= 0}
                fullWidth
              >
                Aggiungi
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Lista prodotti selezionati */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Prodotti Selezionati
          </Typography>
          
          {quoteItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Nessun prodotto selezionato
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Prodotto</TableCell>
                    <TableCell align="center">Quantità</TableCell>
                    <TableCell align="right">Prezzo Unit.</TableCell>
                    <TableCell align="center">Sconto €</TableCell>
                    <TableCell align="right">Subtotale</TableCell>
                    <TableCell align="center">Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {quoteItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {item.productCode} - {item.productName}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                            {item.isCustom && (
                              <Chip label="Personalizzato" size="small" color="secondary" />
                            )}
                            {item.needsOrder && (
                              <Chip label="Da ordinare" size="small" color="warning" />
                            )}
                            {item.includesLabor && (
                              <Chip label="Con montaggio" size="small" color="primary" />
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          value={item.quantity}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Permetti valori vuoti temporaneamente durante la digitazione
                            if (value === '') {
                              handleUpdateItem(item.id, 'quantity', '');
                            } else {
                              const numValue = parseInt(value);
                              if (!isNaN(numValue) && numValue >= 1) {
                                handleUpdateItem(item.id, 'quantity', numValue);
                              }
                            }
                          }}
                          onBlur={(e) => {
                            // Quando l'utente esce dal campo, assicurati che ci sia almeno 1
                            const value = e.target.value;
                            if (value === '' || parseInt(value) < 1) {
                              handleUpdateItem(item.id, 'quantity', 1);
                            }
                          }}
                          inputProps={{ min: 1, step: 1 }}
                          size="small"
                          sx={{ width: 80 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        €{item.unitPrice ? parseFloat(item.unitPrice).toFixed(2) : '0.00'}
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          value={item.discount}
                          onChange={(e) => handleUpdateItem(item.id, 'discount', parseFloat(e.target.value) || 0)}
                          inputProps={{ min: 0, step: 0.01 }}
                          size="small"
                          sx={{ width: 100 }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">€</InputAdornment>
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        €{((item.quantity * parseFloat(item.unitPrice)) - (parseFloat(item.discount) || 0)).toFixed(2)}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          {item.isCustom && (
                            <IconButton
                              size="small"
                              onClick={() => handleEditCustomItem(item)}
                              color="primary"
                              title="Modifica articolo personalizzato"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveItem(item.id)}
                            color="error"
                            title="Rimuovi articolo"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Totali */}
        {quoteItems.length > 0 && (
          <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Subtotale:
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body1" align="right">
                  €{totals.subtotal.toFixed(2)}
                </Typography>
              </Grid>
              
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  IVA ({totals.taxRate}%) inclusa:
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body1" align="right">
                  €{totals.taxAmount.toFixed(2)}
                </Typography>
              </Grid>
              
              <Grid item xs={6} md={3}>
                <Typography variant="h6" color="primary">
                  Totale:
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="h6" color="primary" align="right">
                  €{totals.total.toFixed(2)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      {/* Componente di stampa nascosto */}
      {quoteItems.length > 0 && (
        <Box sx={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
          <QuotePrint
            quote={{ quoteNumber, description: `Preventivo manutenzione per ${vehicle?.brand} ${vehicle?.model}` }}
            customer={customer}
            vehicle={vehicle}
            items={quoteItems}
            totals={totals}
          />
        </Box>
      )}

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} startIcon={<CloseIcon />}>
          Annulla
        </Button>
        <Button
          onClick={handlePrint}
          startIcon={<PrintIcon />}
          variant="outlined"
          disabled={quoteItems.length === 0}
        >
          Stampa
        </Button>
        <Button
          onClick={handleSave}
          startIcon={<SaveIcon />}
          variant="contained"
          disabled={loading || quoteItems.length === 0}
        >
          {loading ? <CircularProgress size={24} /> : (editMode ? 'Aggiorna' : (saveOnly ? 'Salva' : 'Inserisci e salva'))}
        </Button>
      </DialogActions>

      {/* Dialog per articolo personalizzato */}
      <Dialog open={openCustomProductDialog} onClose={handleCloseCustomProductDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{customProductMode === 'edit' ? 'Modifica Articolo Personalizzato' : 'Aggiungi Articolo Personalizzato'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {customProductMode === 'edit' 
              ? 'Modifica i campi dell\'articolo personalizzato' 
              : 'Utilizza questo form per aggiungere un articolo personalizzato che non è presente in magazzino'
            }
          </Typography>
          
          <TextField
            name="code"
            label="Codice articolo *"
            fullWidth
            value={customProduct.code}
            onChange={(e) => {
              const input = e.target;
              const cursorPosition = input.selectionStart;
              const newValue = e.target.value.toUpperCase();
              
              // Aggiorna lo stato
              handleCustomProductChange({
                target: {
                  name: e.target.name,
                  value: newValue
                }
              });
              
              // Ripristina la posizione del cursore dopo il re-render
              setTimeout(() => {
                input.setSelectionRange(cursorPosition, cursorPosition);
              }, 0);
            }}
            margin="normal"
            error={!!customProductErrors.code}
            helperText={customProductErrors.code}
            required
          />
          
          <TextField
            name="description"
            label="Descrizione articolo *"
            fullWidth
            value={customProduct.description}
            onChange={(e) => {
              const input = e.target;
              const cursorPosition = input.selectionStart;
              const newValue = e.target.value.toUpperCase();
              
              // Aggiorna lo stato
              handleCustomProductChange({
                target: {
                  name: e.target.name,
                  value: newValue
                }
              });
              
              // Ripristina la posizione del cursore dopo il re-render
              setTimeout(() => {
                input.setSelectionRange(cursorPosition, cursorPosition);
              }, 0);
            }}
            margin="normal"
            error={!!customProductErrors.description}
            helperText={customProductErrors.description}
            required
            multiline
            rows={3}
          />
          
          <TextField
            name="unitPrice"
            label="Prezzo unitario (€) *"
            fullWidth
            value={customProduct.unitPrice}
            onChange={handleCustomProductChange}
            margin="normal"
            type="number"
            inputProps={{ 
              min: 0, 
              step: 0.01,
              inputMode: 'decimal'
            }}
            error={!!customProductErrors.unitPrice}
            helperText={customProductErrors.unitPrice}
            required
          />
          
          <TextField
            name="costPrice"
            label="Prezzo di costo (€) *"
            fullWidth
            value={customProduct.costPrice}
            onChange={handleCustomProductChange}
            margin="normal"
            type="number"
            inputProps={{ 
              min: 0, 
              step: 0.01,
              inputMode: 'decimal'
            }}
            error={!!customProductErrors.costPrice}
            helperText={customProductErrors.costPrice}
            required
          />
          
          <TextField
            name="quantity"
            label="Quantità *"
            fullWidth
            value={customProduct.quantity}
            onChange={(e) => {
              const value = e.target.value;
              // Permetti valori vuoti temporaneamente durante la digitazione
              if (value === '') {
                handleCustomProductChange({
                  target: { name: 'quantity', value: '' }
                });
              } else {
                const numValue = parseInt(value);
                if (!isNaN(numValue) && numValue >= 1) {
                  handleCustomProductChange({
                    target: { name: 'quantity', value: numValue }
                  });
                }
              }
            }}
            onBlur={(e) => {
              // Quando l'utente esce dal campo, assicurati che ci sia almeno 1
              if (customProduct.quantity === '' || customProduct.quantity < 1) {
                handleCustomProductChange({
                  target: { name: 'quantity', value: 1 }
                });
              }
            }}
            margin="normal"
            type="number"
            inputProps={{ 
              min: 1, 
              step: 1
            }}
            error={!!customProductErrors.quantity}
            helperText={customProductErrors.quantity}
            required
          />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={customProduct.needsOrder}
                onChange={(e) => setCustomProduct(prev => ({ ...prev, needsOrder: e.target.checked }))}
                color="primary"
              />
            }
            label="Articolo da ordinare"
            sx={{ mt: 2 }}
          />
          
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="textSecondary">
              * Campi obbligatori
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCustomProductDialog}>Annulla</Button>
          <Button 
            onClick={handleAddCustomProduct} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : (customProductMode === 'edit' ? 'Aggiorna' : 'Aggiungi')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Advanced Product Search Dialog */}
      <Dialog
        open={openAdvancedSearch}
        onClose={handleCloseAdvancedSearch}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SearchIcon color="primary" />
            <Typography variant="h6">Ricerca Avanzata Prodotti</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Cerca per nome, codice o descrizione..."
            value={advancedSearchTerm}
            onChange={handleAdvancedSearchChange}
            sx={{ mb: 2, mt: 1 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {selectedItems.length} articoli selezionati (solo giacenza disponibile)
          </Typography>

          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {advancedSearchResults.map((product) => {
              const isSelected = selectedItems.find(item => item.id === product.id);
              const selectedItem = isSelected ? selectedItems.find(item => item.id === product.id) : null;
              
              return (
                <Paper key={product.id} sx={{ mb: 1, bgcolor: isSelected ? 'action.selected' : 'background.paper' }}>
                  <ListItem>
                    <Checkbox
                      checked={!!isSelected}
                      onChange={() => handleToggleItem(product)}
                    />
                    <ListItemAvatar>
                      <Avatar
                        variant="rounded"
                        src={getProductImage(product.image)}
                        alt={product.name}
                        sx={{
                          width: 56,
                          height: 56,
                          bgcolor: 'grey.200',
                          '& img': {
                            objectFit: 'contain',
                            p: 0.5
                          }
                        }}
                        imgProps={{
                          onError: (e) => {
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNFRUVFRUUiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzY2NiIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0Ij5JbW1hZ2luZSBub24gZGlzcG9uaWJpbGU8L3RleHQ+PC9zdmc+';
                          }
                        }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ color: '#000000' }}>
                          {product.code} - {product.name}
                        </Typography>
                      }
                      secondary={
                        <Box component="span">
                          <Typography variant="body2" component="span" color="text.secondary">
                            Prezzo: €{product.sellingPrice ? parseFloat(product.sellingPrice).toFixed(2) : '0.00'}
                            {product.sellingPriceWithAssembly && (
                              <Typography variant="body2" component="span" color="primary" sx={{ ml: 0.5 }}>
                                {' | '}Con montaggio: €{product.sellingPriceWithAssembly ? parseFloat(product.sellingPriceWithAssembly).toFixed(2) : '0.00'}
                              </Typography>
                            )}
                            {' | '}Stock: {product.stock || 0}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  
                  {isSelected && (
                    <Box sx={{ px: 2, pb: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={6}>
                          <TextField
                            label="Quantità"
                            type="number"
                            size="small"
                            fullWidth
                            value={selectedItem.quantity}
                            onChange={(e) => handleItemQuantityChange(product.id, e.target.value)}
                            onBlur={(e) => handleItemQuantityBlur(product.id, e.target.value)}
                            InputProps={{ inputProps: { min: 1, step: 1 } }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          {!product.isLabor && (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedItem.includesLabor}
                                  onChange={(e) => handleItemLaborChange(product.id, e.target.checked)}
                                  color="primary"
                                />
                              }
                              label="Include manodopera"
                            />
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </Paper>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAdvancedSearch}>
            Annulla
          </Button>
          <Button
            onClick={handleAddSelectedItems}
            variant="contained"
            color="primary"
            disabled={selectedItems.length === 0}
            startIcon={<AddIcon />}
          >
            Aggiungi {selectedItems.length > 0 ? `(${selectedItems.length})` : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* B2B Shop Search Dialog */}
      <Dialog
        open={openB2bSearch}
        onClose={handleCloseB2bSearch}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StorefrontIcon color="secondary" />
            <Typography variant="h6">Ricerca nello Shop B2B</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Cerca prodotti nel listino B2B e aggiungili al preventivo. Gli articoli saranno flaggati come &quot;Articolo da ordinare&quot;.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Cerca per codice o descrizione..."
              value={b2bSearchTerm}
              onChange={(e) => setB2bSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), searchB2b(b2bSearchTerm))}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
            <Button
              variant="outlined"
              onClick={() => searchB2b(b2bSearchTerm)}
              disabled={b2bLoading || !b2bSearchTerm.trim()}
            >
              {b2bLoading ? <CircularProgress size={24} /> : 'Cerca'}
            </Button>
          </Box>
          {b2bError && (
            <Alert severity="info" onClose={() => setB2bError(null)} sx={{ mb: 1 }}>
              {b2bError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {b2bSelectedItems.length} articoli selezionati
          </Typography>
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {b2bResults.map((product) => {
              const key = product.id || product.code;
              const sel = b2bSelectedItems.find(item => (item.product.id || item.product.code) === key);
              const isSelected = !!sel;
              const sellingPrice = parseFloat(product.sellingPrice) || 0;
              const purchasePrice = parseFloat(product.purchasePrice) || 0;
              const imageUrl = (product.image || '').trim();
              return (
                <Paper key={key} sx={{ mb: 1, bgcolor: isSelected ? 'action.selected' : 'background.paper' }}>
                  <ListItem>
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleToggleB2bItem(product)}
                    />
                    <ListItemAvatar>
                      <Avatar
                        variant="rounded"
                        src={imageUrl || undefined}
                        alt={product.name}
                        sx={{
                          width: 56,
                          height: 56,
                          bgcolor: 'grey.200',
                          '& img': { objectFit: 'contain', p: 0.5 }
                        }}
                        imgProps={{
                          onError: (e) => { e.target.style.display = 'none'; }
                        }}
                      >
                        {!imageUrl ? (product.name || product.code || '').charAt(0) : null}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ color: '#000' }}>
                          {product.code} - {product.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          Costo: €{purchasePrice.toFixed(2)} · Vendita: €{sellingPrice.toFixed(2)}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {isSelected && (
                    <Box sx={{ px: 2, pb: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={3}>
                          <TextField
                            label="Quantità"
                            type="number"
                            size="small"
                            fullWidth
                            value={sel.quantity}
                            onChange={(e) => handleB2bQuantityChange(key, e.target.value)}
                            onBlur={() => handleB2bQuantityBlur(key)}
                            inputProps={{ min: 1, step: 1 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={sel.includesLabor}
                                onChange={(e) => handleB2bLaborChange(key, e.target.checked)}
                                color="primary"
                              />
                            }
                            label="Con montaggio"
                          />
                        </Grid>
                        {sel.includesLabor && (
                          <Grid item xs={12} sm={5}>
                            <TextField
                              label="Prezzo montaggio (€)"
                              type="number"
                              size="small"
                              fullWidth
                              value={sel.manualAssemblyPrice}
                              onChange={(e) => handleB2bAssemblyPriceChange(key, e.target.value)}
                              inputProps={{ min: 0, step: 0.01 }}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  )}
                </Paper>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseB2bSearch}>Annulla</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={b2bSelectedItems.length === 0}
            startIcon={<AddIcon />}
            onClick={handleAddB2bItemsToQuote}
          >
            Aggiungi al preventivo{b2bSelectedItems.length > 0 ? ` (${b2bSelectedItems.length})` : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default QuoteDialog;
